import { useEffect } from 'react';

export interface SEOProps {
  title?: string;
  description?: string;
  pathname?: string;
  image?: string;
  type?: 'website' | 'article' | 'event' | 'organization' | 'localBusiness';
  publishedAt?: string;
  modifiedAt?: string;
  author?: string;
  tags?: string[];
  noindex?: boolean;
  keywords?: string[];
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const defaultSiteConfig = {
  name: 'Sternwarte Mannheim',
  titleTemplate: '%s | Sternwarte Mannheim',
  defaultTitle: 'Alte Sternwarte Mannheim - Historische Sternwarte & Kulturzentrum',
  defaultDescription:
    'Erleben Sie Führungen, astronomische Beobachtungen und kulturelle Veranstaltungen in einem der ältesten Gebäude Mannheims. Seit 1772.',
  siteUrl: 'https://sternwarte-mannheim.de',
  defaultImage: '/og-image.svg',
  organization: {
    name: 'Verein Stadtbild Mannheim e.V.',
    fullName: 'Verein Stadtbild Mannheim e.V. - Aktionsbündnis Alte Sternwarte',
    email: 'kontakt@sternwarte-mannheim.de',
    address: {
      street: 'A4,6',
      city: 'Mannheim',
      postalCode: '68159',
      country: 'DE',
    },
  },
};

// Generate Organization structured data
const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: defaultSiteConfig.organization.name,
  alternateName: defaultSiteConfig.name,
  description: defaultSiteConfig.defaultDescription,
  url: defaultSiteConfig.siteUrl,
  logo: `${defaultSiteConfig.siteUrl}/vite.svg`,
  email: defaultSiteConfig.organization.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: defaultSiteConfig.organization.address.street,
    addressLocality: defaultSiteConfig.organization.address.city,
    postalCode: defaultSiteConfig.organization.address.postalCode,
    addressCountry: defaultSiteConfig.organization.address.country,
  },
});

// Generate LocalBusiness structured data
const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['TouristAttraction', 'LandmarksOrHistoricalBuildings', 'LocalBusiness'],
  name: defaultSiteConfig.name,
  alternateName: 'Alte Sternwarte Mannheim',
  description: defaultSiteConfig.defaultDescription,
  url: defaultSiteConfig.siteUrl,
  email: defaultSiteConfig.organization.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: defaultSiteConfig.organization.address.street,
    addressLocality: defaultSiteConfig.organization.address.city,
    postalCode: defaultSiteConfig.organization.address.postalCode,
    addressCountry: defaultSiteConfig.organization.address.country,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 49.4833,
    longitude: 8.4631,
  },
  priceRange: '€',
  isAccessibleForFree: false,
  publicAccess: true,
});

// Generate Article structured data
const generateArticleSchema = (props: SEOProps) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: props.title || defaultSiteConfig.defaultTitle,
  description: props.description || defaultSiteConfig.defaultDescription,
  url: `${defaultSiteConfig.siteUrl}${props.pathname || ''}`,
  image: props.image
    ? `${defaultSiteConfig.siteUrl}${props.image}`
    : `${defaultSiteConfig.siteUrl}${defaultSiteConfig.defaultImage}`,
  author: {
    '@type': 'Organization',
    name: props.author || defaultSiteConfig.organization.fullName,
  },
  publisher: {
    '@type': 'Organization',
    name: defaultSiteConfig.organization.name,
    logo: {
      '@type': 'ImageObject',
      url: `${defaultSiteConfig.siteUrl}/vite.svg`,
    },
  },
  datePublished: props.publishedAt || new Date().toISOString(),
  dateModified: props.modifiedAt || props.publishedAt || new Date().toISOString(),
  keywords: props.tags?.join(', ') || '',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${defaultSiteConfig.siteUrl}${props.pathname || ''}`,
  },
});

// Generate Event structured data
const generateEventSchema = (props: SEOProps) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: props.title || defaultSiteConfig.defaultTitle,
  description: props.description || defaultSiteConfig.defaultDescription,
  url: `${defaultSiteConfig.siteUrl}${props.pathname || ''}`,
  image: props.image
    ? `${defaultSiteConfig.siteUrl}${props.image}`
    : `${defaultSiteConfig.siteUrl}${defaultSiteConfig.defaultImage}`,
  startDate: props.publishedAt || new Date().toISOString(),
  endDate: props.modifiedAt || props.publishedAt || new Date().toISOString(),
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  location: {
    '@type': 'Place',
    name: defaultSiteConfig.name,
    address: {
      '@type': 'PostalAddress',
      streetAddress: defaultSiteConfig.organization.address.street,
      addressLocality: defaultSiteConfig.organization.address.city,
      postalCode: defaultSiteConfig.organization.address.postalCode,
      addressCountry: defaultSiteConfig.organization.address.country,
    },
  },
  organizer: {
    '@type': 'Organization',
    name: defaultSiteConfig.organization.name,
    url: defaultSiteConfig.siteUrl,
  },
  offers: {
    '@type': 'Offer',
    url: `${defaultSiteConfig.siteUrl}${props.pathname || ''}`,
    price: '0',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/InStock',
    validFrom: props.publishedAt || new Date().toISOString(),
  },
});

// Generate WebSite structured data for search box
const generateWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: defaultSiteConfig.name,
  url: defaultSiteConfig.siteUrl,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${defaultSiteConfig.siteUrl}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

// Generate BreadcrumbList structured data
const generateBreadcrumbSchema = (pathname: string) => {
  const pathSegments = pathname.split('/').filter(Boolean);
  const breadcrumbItems = pathSegments.map((segment, index) => {
    const url = `${defaultSiteConfig.siteUrl}/${pathSegments.slice(0, index + 1).join('/')}`;
    return {
      '@type': 'ListItem',
      position: index + 2,
      name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
      item: url,
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Startseite',
        item: defaultSiteConfig.siteUrl,
      },
      ...breadcrumbItems,
    ],
  };
};

export default function SEO({
  title,
  description,
  pathname = '',
  image,
  type = 'website',
  publishedAt,
  modifiedAt,
  author,
  tags,
  noindex = false,
  keywords = [],
  structuredData,
}: SEOProps) {
  const fullTitle = title
    ? defaultSiteConfig.titleTemplate.replace('%s', title)
    : defaultSiteConfig.defaultTitle;

  const metaDescription = description || defaultSiteConfig.defaultDescription;
  const canonicalUrl = `${defaultSiteConfig.siteUrl}${pathname}`;
  const ogImage = image
    ? image.startsWith('http')
      ? image
      : `${defaultSiteConfig.siteUrl}${image}`
    : `${defaultSiteConfig.siteUrl}${defaultSiteConfig.defaultImage}`;

  // Generate structured data based on type
  const defaultStructuredData: Record<string, unknown>[] = [];

  // Always include website schema on homepage
  if (pathname === '' || pathname === '/') {
    defaultStructuredData.push(generateWebSiteSchema());
    defaultStructuredData.push(generateOrganizationSchema());
  }

  // Add type-specific schemas
  switch (type) {
    case 'article':
      defaultStructuredData.push(generateArticleSchema({ title, description, pathname, image, publishedAt, modifiedAt, author, tags }));
      break;
    case 'event':
      defaultStructuredData.push(generateEventSchema({ title, description, pathname, image, publishedAt, modifiedAt }));
      break;
    case 'organization':
      defaultStructuredData.push(generateOrganizationSchema());
      break;
    case 'localBusiness':
      defaultStructuredData.push(generateLocalBusinessSchema());
      break;
    default:
      // For regular pages, include local business schema
      if (pathname !== '' && pathname !== '/') {
        defaultStructuredData.push(generateLocalBusinessSchema());
      }
  }

  // Add breadcrumb schema for non-home pages
  if (pathname && pathname !== '/' && pathname !== '') {
    defaultStructuredData.push(generateBreadcrumbSchema(pathname));
  }

  // Merge with custom structured data if provided
  const finalStructuredData = structuredData
    ? Array.isArray(structuredData)
      ? [...defaultStructuredData, ...structuredData]
      : [...defaultStructuredData, structuredData]
    : defaultStructuredData;

  const allKeywords = [...keywords, ...(tags || [])];

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update link tags
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Basic Meta Tags
    updateMetaTag('description', metaDescription);
    updateLinkTag('canonical', canonicalUrl);

    // Robots
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Keywords
    if (allKeywords.length > 0) {
      updateMetaTag('keywords', allKeywords.join(', '));
    }

    // Author
    if (author) {
      updateMetaTag('author', author);
    }

    // Open Graph
    updateMetaTag('og:type', type === 'article' ? 'article' : type === 'event' ? 'event' : 'website', true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', metaDescription, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:alt', title || defaultSiteConfig.name, true);
    updateMetaTag('og:locale', 'de_DE', true);
    updateMetaTag('og:site_name', defaultSiteConfig.name, true);

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', canonicalUrl);
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', metaDescription);
    updateMetaTag('twitter:image', ogImage);
    updateMetaTag('twitter:image:alt', title || defaultSiteConfig.name);

    // Update or create structured data scripts
    const existingScripts = document.querySelectorAll('script[data-seo="structured-data"]');
    existingScripts.forEach(script => script.remove());

    finalStructuredData.forEach((data) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo', 'structured-data');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    // Cleanup
    return () => {
      // Note: We don't remove meta tags on unmount to avoid flickering
      // when navigating between pages
    };
  }, [fullTitle, metaDescription, canonicalUrl, ogImage, noindex, allKeywords, author, type, title, finalStructuredData]);

  return null;
}
